import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {Client} from "@stomp/stompjs";

import {useRayzayUIController} from "./index";
import {getAuthTokenKey, getBackendUrl} from "../utils/config";
import {getMyNotifications, getMyUnreadCount} from "../api/notification/notificationApiClient";
import {showInfo} from "../utils/notificationUtils";

/**
 * NotificationProvider — single place that:
 *   - keeps an authenticated STOMP/WebSocket connection while the user
 *     is logged in
 *   - subscribes to /user/queue/notifications (per-user destination)
 *   - tracks the unread count + most recent notifications for the
 *     navbar bell
 *   - shows a toast when a new one arrives
 *
 * Wraps the app in App.js, below RayzayUIControllerProvider. Consumers
 * use `useNotifications()`.
 */

const NotificationContext = createContext(null);

const STOMP_DESTINATION = "/user/queue/notifications";
const RECENT_LIMIT = 10;

function buildWsUrl() {
    const base = getBackendUrl();
    // Convert http:// → ws:// or https:// → wss:// so the STOMP client
    // talks to /ws on the same host as the REST API.
    return base.replace(/^http/, "ws") + "/ws";
}

export function NotificationProvider({children}) {
    const [controller] = useRayzayUIController();
    const {isAuthenticated, loggedInUserDetail} = controller;

    const [unreadCount, setUnreadCount] = useState(0);
    const [recent, setRecent] = useState([]);
    const [connected, setConnected] = useState(false);

    const clientRef = useRef(null);

    // Identity as a stable primitive, not the object. See the effect below:
    // the object is reassigned by unrelated flows (profile refresh, auth
    // re-check) while still describing the same user, so depending on it
    // reconnects constantly — but depending on neither misses the login race.
    const userKey = loggedInUserDetail?.username ?? null;

    // Pull the initial unread count when the user is authenticated.
    const refreshUnreadCount = useCallback(async () => {
        if (!isAuthenticated) return;
        const count = await getMyUnreadCount();
        setUnreadCount(count);
    }, [isAuthenticated]);

    // Seed the bell from the server. Without this `recent` only ever held
    // notifications that arrived over the websocket *during this page's
    // lifetime*, so the dropdown was empty on every fresh load and after every
    // navigation-triggered remount — even with unread items sitting in the
    // table. Live pushes still prepend onto whatever this loaded.
    const refreshRecent = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const page = await getMyNotifications({page: 0, size: RECENT_LIMIT});
            setRecent(page?.content || []);
        } catch {
            // Non-fatal: the bell falls back to live-only, as before.
        }
    }, [isAuthenticated]);

    useEffect(() => {
        refreshUnreadCount().then();
        refreshRecent().then();
    }, [refreshUnreadCount, refreshRecent]);

    // Manage the STOMP lifecycle on auth changes.
    //
    // Depending on the whole `loggedInUserDetail` object tore the socket down
    // every time an unrelated flow (profile refresh, periodic auth-check)
    // reassigned that controller field, flipping setConnected(false→true) and
    // re-rendering every consumer — inputs blinking, dropdowns closing.
    //
    // Depending on `isAuthenticated` alone fixed the churn but broke login:
    // sign-in calls setAuthentication(true) and only then awaits the user
    // detail, so this effect ran while loggedInUserDetail was still null, hit
    // the guard below, and never re-ran — the web client silently never
    // connected. (A page reload worked, because the controller seeds both
    // fields from localStorage in the same initial state.)
    //
    // `userKey` is the middle ground: it changes when the user actually
    // changes, and not when the object is merely reassigned.
    useEffect(() => {
        if (!isAuthenticated || !userKey) {
            // Disconnect any existing client.
            if (clientRef.current) {
                clientRef.current.deactivate();
                clientRef.current = null;
                setConnected(false);
            }
            return undefined;
        }

        const token = localStorage.getItem(getAuthTokenKey());
        if (!token) return undefined;

        const client = new Client({
            brokerURL: buildWsUrl(),
            connectHeaders: {Authorization: `Bearer ${token}`},
            reconnectDelay: 5000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
        });

        client.onConnect = () => {
            setConnected(true);
            client.subscribe(STOMP_DESTINATION, (frame) => {
                try {
                    const dto = JSON.parse(frame.body);
                    setRecent((prev) => [dto, ...prev.filter((n) => n.id !== dto.id)].slice(0, RECENT_LIMIT));
                    setUnreadCount((c) => c + 1);
                    showInfo(dto.title || "New notification");
                } catch {
                    // Malformed payload — ignore.
                }
            });
        };

        client.onStompError = () => setConnected(false);
        client.onWebSocketClose = () => setConnected(false);

        client.activate();
        clientRef.current = client;

        return () => {
            client.deactivate();
            clientRef.current = null;
            setConnected(false);
        };
    }, [isAuthenticated, userKey]);

    const decrementUnread = useCallback((n = 1) => {
        setUnreadCount((c) => Math.max(0, c - n));
    }, []);

    const clearUnread = useCallback(() => setUnreadCount(0), []);

    const upsertRecent = useCallback((dto) => {
        setRecent((prev) => {
            const filtered = prev.filter((n) => n.id !== dto.id);
            return [dto, ...filtered].slice(0, RECENT_LIMIT);
        });
    }, []);

    const value = useMemo(
        () => ({
            unreadCount,
            recent,
            connected,
            refreshUnreadCount,
            refreshRecent,
            decrementUnread,
            clearUnread,
            upsertRecent,
        }),
        [unreadCount, recent, connected, refreshUnreadCount, refreshRecent, decrementUnread, clearUnread, upsertRecent],
    );

    return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

NotificationProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export function useNotifications() {
    const ctx = useContext(NotificationContext);
    if (!ctx) {
        throw new Error("useNotifications must be used inside a NotificationProvider");
    }
    return ctx;
}
